import { useEffect, useState } from 'react';
import { api, type CustomFieldDef, type TagDef, type PipelineWithStages } from '../api';
import type { ObjectDef } from '../config';

interface Props {
  object: ObjectDef;
  recordId?: string;
  onClose: () => void;
  onSaved: () => void;
}

const NEEDS_PIPELINE = (o: ObjectDef) => o.fields.some((f) => f.type === 'pipeline' || f.type === 'stage');

export function RecordForm({ object, recordId, onClose, onSaved }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [defs, setDefs] = useState<CustomFieldDef[]>([]);
  const [tags, setTags] = useState<TagDef[]>([]);
  const [pipelines, setPipelines] = useState<PipelineWithStages[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      setDefs(await api.get<CustomFieldDef[]>(`/api/custom-fields/${object.key}`));
      setTags(await api.get<TagDef[]>('/api/tags'));
      if (NEEDS_PIPELINE(object)) setPipelines(await api.get<PipelineWithStages[]>('/api/pipelines'));
      if (recordId) {
        const rec = await api.get<Record<string, unknown>>(`/api/${object.path}/${recordId}`);
        const v: Record<string, string> = {};
        for (const f of object.fields) {
          const raw = rec[f.key];
          if (raw != null) v[f.key] = String(raw).slice(0, f.type === 'date' ? 10 : 16);
        }
        setValues(v);
        const cf = (rec.customFields as Record<string, unknown>) ?? {};
        const cv: Record<string, string> = {};
        for (const [k, val] of Object.entries(cf)) if (val != null) cv[k] = String(val);
        setCustomValues(cv);
        const recTags = (rec.tags as { id: string }[]) ?? [];
        setTagIds(recTags.map((t) => t.id));
      }
    })().catch(() => setError('Failed to load form data.'));
  }, [object, recordId]);

  function set(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body: Record<string, unknown> = {};
    for (const f of object.fields) {
      const val = values[f.key];
      if (val !== undefined && val !== '') body[f.key] = val;
    }
    const customFields: Record<string, string> = {};
    for (const [k, val] of Object.entries(customValues)) if (val !== '') customFields[k] = val;
    if (Object.keys(customFields).length) body.customFields = customFields;
    body.tagIds = tagIds;
    try {
      if (recordId) await api.patch(`/api/${object.path}/${recordId}`, body);
      else await api.post(`/api/${object.path}`, body);
      onSaved();
    } catch {
      setError('Save failed. Check required fields and references.');
      setSaving(false);
    }
  }

  const selectedPipeline = pipelines.find((p) => p.id === values.pipelineId) ?? pipelines[0];

  return (
    <div className="overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <h2>{recordId ? 'Edit' : 'New'} {object.label.replace(/s$/, '')}</h2>
        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
        <form className="stack" onSubmit={submit}>
          {object.fields.map((f) => {
            if (f.type === 'pipeline') {
              return (
                <label key={f.key} className="field">
                  <span>{f.label}</span>
                  <select value={values.pipelineId ?? ''} onChange={(e) => set('pipelineId', e.target.value)}>
                    <option value="">Select…</option>
                    {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
              );
            }
            if (f.type === 'stage') {
              return (
                <label key={f.key} className="field">
                  <span>{f.label}</span>
                  <select value={values.stageId ?? ''} onChange={(e) => set('stageId', e.target.value)}>
                    <option value="">Select…</option>
                    {(selectedPipeline?.stages ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
              );
            }
            return (
              <label key={f.key} className="field">
                <span>{f.label}</span>
                {f.type === 'textarea' ? (
                  <textarea value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} />
                ) : (
                  <input
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'datetime' ? 'datetime-local' : 'text'}
                    value={values[f.key] ?? ''}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                )}
              </label>
            );
          })}

          {defs.length > 0 && (
            <fieldset className="field">
              <legend className="muted">Custom fields</legend>
              {defs.map((d) => (
                <label key={d.id} className="field">
                  <span>{d.label}</span>
                  <input
                    type={d.dataType === 'number' ? 'number' : d.dataType === 'date' ? 'date' : 'text'}
                    value={customValues[d.fieldKey] ?? ''}
                    onChange={(e) => setCustomValues((p) => ({ ...p, [d.fieldKey]: e.target.value }))}
                  />
                </label>
              ))}
            </fieldset>
          )}

          {tags.length > 0 && (
            <div className="field">
              <span className="muted">Tags</span>
              <div className="row">
                {tags.map((t) => {
                  const on = tagIds.includes(t.id);
                  return (
                    <button
                      type="button"
                      key={t.id}
                      className={on ? 'tag tag-on' : 'tag'}
                      onClick={() => setTagIds((prev) => (on ? prev.filter((x) => x !== t.id) : [...prev, t.id]))}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="row">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
